import {Injectable, Injector}             from '@angular/core';
import {TaskManagementDataProviderBase}   from './task-management-data-provider.base';
import {ITaskManagementDataProvider}      from './task-management-data-provider.interface';
import {TaskManagementConstants}          from '../task-management.constants';
import {
  IExportFormatsComboData
}                                         from '../components/wizard/wizard-ui-config/task-management-wizard-details.config';
import {ILegend}                          from '../../stores/map/map.legend.store';
import {MapStore}                         from '../../stores/map/map.store';
import {ApLegendScrollerService}          from '../../map/components/layer-controller/ap-legend-scroller.service';
import {NUTRIENT_PLANNING}                from '../../ap-map/layers/layer.legends';
import {GetElementService}                from '../../ap-utils/service/ap-get-element.service';
import {BasicFertilisationSummariesStore} from '../../stores/taskmanagement/basic.fertilisation.summaries.store';
import {Observable}                       from 'rxjs';
import {FieldStore}                       from '../../stores/farm/field.store';
import {ApGetCropService}                 from '../../stores/services/ap-get-crop.service';
import {BaseFertilizationService}         from '../../services/data/base-fertilization.service';
import {CampaignYearService}              from '../../services/data/campaign-year.service';
import {BaseFertilizationCommonService}   from '../../nutrient-management/service/base-fertilization-common.service';
import {BaseFertilizationPlansLegendData} from '../../ap-map/layers/types/nutrient-planning.types';
import {OperationModesEnum}               from '../../stores/common/operation-modes.store';
import {ApplicationModesEnum}             from '../../stores/common/appl-modes.store';
import {IGridStatus}                      from '../../ap-interface/interfaces/ap-grid-status.interface';
import {MapLayerTooltipFunction}          from 'src/app/ap-map/layers/map-factory-layer';
import IGuid = System.IGuid;
import ITaskManagementPlanSummary = Data.TaskManagement.ITaskManagementPlanSummary;
import ITaskManagementPlanBook = Data.TaskManagement.ITaskManagementPlanBook;

@Injectable({providedIn: 'root'})
export class TaskManagementDataProviderBasicfert extends TaskManagementDataProviderBase implements ITaskManagementDataProvider {
  private readonly mapProviderBaseName = 'pro_nutrient_planning_';
  private readonly mapBaseName = 'map_nutrient_planning_';

  constructor(injector: Injector,
              private mapStore: MapStore,
              private fieldStore: FieldStore,
              private cropService: ApGetCropService,
              private elementService: GetElementService,
              private campaignYearService: CampaignYearService,
              private legendScrollerService: ApLegendScrollerService,
              private baseFertilizationService: BaseFertilizationService,
              private baseFertilizationCommonService: BaseFertilizationCommonService,
              private basicFertilisationSummariesStore: BasicFertilisationSummariesStore) {
    super(injector);
  }

  public getComponentUrl(): string {
    return 'nutrients/planning';
  }

  public getStatus(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): IGridStatus {
    const baseStatus = super.getStatus(summaryOrPlanBook);
    return {
      state: baseStatus.state,
      class: this.nutrientService.getFertilizerStatus(summaryOrPlanBook.ApplicationMode, false)?.class,
      description: this.nutrientService.getFertilizerStatusDescription(summaryOrPlanBook.ApplicationMode, false)
    };
  }

  public getModuleText(_: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    return this.translationStore.FindTranslationForSelectedLanguage('Task_Mgmt_GD');
  }

  public getCropName(summaryOrPlanBook: ITaskManagementPlanBook): string {
    const field = this.fieldStore.getFieldById(summaryOrPlanBook.FieldId);
    const applicationDate = new Date(summaryOrPlanBook.ApplDate);
    const campaignYear = this.campaignYearService.getCampaignYearByDate(applicationDate);
    const cropRotation = this.cropService.getCropRotationByYear(field?.Id, campaignYear.CampaignYear);
    const cropName = this.cropService.getCropNameFromCropRotationYield(cropRotation);
    return cropName ?? '';
  }

  public getProductName(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (!summaryOrPlanBook || summaryOrPlanBook.ProductId === 0) {
      return this.translationStore.FindTranslationForSelectedLanguage('Nutrient__PureNutrient');
    }
    const product = this.fertilizerStore.getFertilizer(summaryOrPlanBook.ProductId);
    if (!product) {
      return '-';
    }
    return product.Prodname;
  }

  public getOperationModeText(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (summaryOrPlanBook?.OperationMode?.Key) {
      return undefined;
    }
    // TODO: temporary work around because of changes for ApplicationMap in N-Fertilization module
    if (summaryOrPlanBook.OperationMode.Key === OperationModesEnum.ApplicationMap) {
      return this.translationStore.FindTranslationForSelectedLanguage(ApplicationModesEnum.Variable);
    }
    return this.translationStore.FindTranslationForSelectedLanguage(summaryOrPlanBook.OperationMode.Key);
  }

  public getApplRate(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook) {
      return undefined;
    }
    // when displaying single planbooks (wizard) => show product rate
    if (!this.isSummary(summaryOrPlanBook)) {
      const planBook = summaryOrPlanBook as ITaskManagementPlanBook;
      return this.getAppRateValue(planBook, 'avg');
    }
    return summaryOrPlanBook.ApplRate;
  }

  public getApplMin(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook || this.isSummary(summaryOrPlanBook)) {
      return undefined;
    }
    // when displaying single planbooks (wizard) => show product rate
    const planBook = summaryOrPlanBook as ITaskManagementPlanBook;
    return this.getAppRateValue(planBook, 'min');
  }

  public getApplMax(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook || this.isSummary(summaryOrPlanBook)) {
      return undefined;
    }
    const planBook = summaryOrPlanBook as ITaskManagementPlanBook;
    return this.getAppRateValue(planBook, 'max');
  }

  /**
   * Method getApplSum calculates Sum of Application values based on either all planbooks provided in summary
   * or only one planBook when provided as second argument
   * @param summaryOrPlanBook Summary to take PlanBooks from or only one plan book
   */
  public getApplSum(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): number | undefined {
    if (!summaryOrPlanBook) {
      return undefined;
    }
    const planBooks = this.isSummary(summaryOrPlanBook)
      ? (summaryOrPlanBook as ITaskManagementPlanSummary).PlanBooks
      : [summaryOrPlanBook as ITaskManagementPlanBook];
    const summaryOperationMode = this.isSummary(summaryOrPlanBook)
      ? (summaryOrPlanBook as ITaskManagementPlanSummary)?.OperationMode?.Key as OperationModesEnum
      : (summaryOrPlanBook as ITaskManagementPlanBook)?.Summary?.OperationMode?.Key as OperationModesEnum;
    let applSum = 0;
    for (const planBookItem of planBooks) {
      let planBookAppl = 0;
      if (planBookItem.ProductId > 0) {
        const planBookOperationMode = planBookItem?.OperationMode?.Key as OperationModesEnum;
        // Workaround for summaries/planbooks with the wrong element (might be due to migration from 4.8)
        const element = summaryOperationMode === OperationModesEnum.Constant && planBookOperationMode === OperationModesEnum.Constant
          ? this.nutrientService.getFertilizerLeadingElement(planBookItem.ProductId)
          : planBookItem.Element?.Id;
        planBookAppl = this.nutrientService.convertNutrientToGoods(planBookItem.ProductId, element, planBookItem.ApplRate * planBookItem.AreaHa);
      } else {
        planBookAppl = planBookItem.ApplRate * planBookItem.AreaHa;
      }
      applSum += planBookAppl;
    }
    return applSum;
  }

  public getProductUnit(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook, perHa = false): string {
    if (this.isSummary(summaryOrPlanBook) && summaryOrPlanBook.ApplUnit?.Id !== 1 && perHa) {
      // There are now only two units at the collective planning level: either kg or % planning.
      // In the case of % planning, the collective planning unit is used.
      // Otherwise, the unit is determined from the product: kg, m³, summaryOrPlanBook, ...
      // However, this is only needed when displaying average values. The total
      // is displayed in the product unit.
      return this.translationStore.FindTranslationForSelectedLanguage(summaryOrPlanBook.ApplUnit?.Key);
    }
    return super.getProductUnitBase(summaryOrPlanBook, perHa);
  }

  public getExportBaseFilename(summary: ITaskManagementPlanSummary, planBook: ITaskManagementPlanBook): string {
    const applDate = this.dateService.dateToMoment(planBook?.ApplDate ? planBook.ApplDate : new Date()).format('YYMMDD');
    const note = (summary?.Remark ? `${this.textProcessorService.replaceSpecialChars(summary.Remark.substring(0, 20))}` : '');
    let filename = `${applDate}${(note?.length > 0 ? '_' : '')}${note}`;
    const productName = this.getProductName(summary);
    if (!!productName && productName !== '-') {
      filename += `_${this.textProcessorService.replaceSpecialNumbersInElements(productName)}`;
    }
    return filename;
  }

  public getExportFormats(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): Array<IExportFormatsComboData> {
    if (!summaryOrPlanBook || !summaryOrPlanBook.ApplicationMode || !summaryOrPlanBook.OperationMode) {
      return [];
    }
    const fertilizer = this.fertilizerStore.getFertilizer(summaryOrPlanBook?.ProductId);
    const exportFormatFilter: string[] = [
      TaskManagementConstants.EXPORT_FORMAT_SHP_POLYGON,
      TaskManagementConstants.EXPORT_FORMAT_ISO
    ];
    if (!fertilizer || !fertilizer.IsOrganic) {
      // for organic fertilizers → no PFBox (due to conversion problems on the PfBox
      exportFormatFilter.push(TaskManagementConstants.EXPORT_FORMAT_PFBOX_RST);
    }
    exportFormatFilter.push(TaskManagementConstants.EXPORT_FORMAT_PDF);
    return super.getFilteredExportFormats(exportFormatFilter);
  }

  public getElementName(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): string {
    if (summaryOrPlanBook.ApplicationMode.Key === ApplicationModesEnum.Constant) {
      return '-';
    }
    return this.elementService.GetElementStringByIElement(summaryOrPlanBook.Element);
  }

  public getMapFactoryUrl(plans: ITaskManagementPlanBook[], selectedLegendKey?: string): string {
    if (!plans || plans.length < 1) {
      return '';
    }
    const elementShort = selectedLegendKey ? selectedLegendKey : this.getElementShort(plans[0]);
    return super.getMapFactoryUrl(plans, `${this.mapProviderBaseName}${elementShort}`, `${this.mapBaseName}${elementShort}`);
  }

  public getElementShort(planBook: ITaskManagementPlanBook | ITaskManagementPlanSummary): string {
    let elementShort = planBook?.Element?.ShortDescription?.toLowerCase();
    if (!elementShort || elementShort === '') {
      elementShort = 'p';
    } else if (elementShort === 'cao') {
      elementShort = 'ph';
    }
    return elementShort;
  }

  public getMapLegend(summary: ITaskManagementPlanSummary, _?: string): () => ILegend {
    if (!summary?.Statistic || !summary.Element?.Id) {
      return () => undefined;
    }
    const summaryData: BaseFertilizationPlansLegendData = {
      ProductId: summary.ProductId,
      ElementId: summary.Element.Id,
      Statistic: summary.Statistic,
      OperationModeId: summary.OperationMode.Id
    };
    return () => this.convertToTaskMgmtLegend(
      NUTRIENT_PLANNING(
        this.translationStore.FindTranslationForSelectedLanguage.bind(this.translationStore),
        this.elementService,
        this.baseFertilizationService,
        this.fertilizerStore,
        summaryData
      )
    );
  }

  public getMapLayerTooltip(): MapLayerTooltipFunction {
    return this.mapLayerTooltipStore.getNutrientPlanningsLayerTooltip();
  }

  public setSelectedGridIdFromPreviousPage(gridItemId: IGuid | undefined): void {
    this.baseFertilizationCommonService.setSelectedSummaryKey(gridItemId);
  }

  public selectLegendItem(summaryOrPlanBook: ITaskManagementPlanSummary | ITaskManagementPlanBook): void {
    // select correct legend nutrient:
    if (this.mapStore.Layers.TaskManagementNutrientLayer.Legend && this.mapStore.Layers.TaskManagementNutrientLayer?.Legend()?.selectionLists?.length > 0) {
      const legendItems = this.mapStore.Layers.TaskManagementNutrientLayer.Legend().selectionLists.FirstOrDefault();
      // Legend items using upper case values for products so we need case invariant comparison
      const selectedLegendElement = (legendItems.values as any[]).Find(_ => _.value.toLowerCase() === this.getElementShort(summaryOrPlanBook).toLowerCase());
      if (selectedLegendElement) {
        legendItems.selectedValue.next(selectedLegendElement);
        this.legendScrollerService.selectionChange.emit({
          legend: this.mapStore.Legends.TrackLegend,
          selectionKeys: this.legendScrollerService.generateSelectionKey(this.mapStore.Legends.TrackLegend).Split('_'),
        });
      }
    }
  }

  public deletePlannings(summaryOrPlanBooks: ITaskManagementPlanSummary | ITaskManagementPlanBook[]): void {
    const summaryId = this.isSummary(summaryOrPlanBooks)
      ? (summaryOrPlanBooks as ITaskManagementPlanSummary).Id
      : (summaryOrPlanBooks as ITaskManagementPlanBook[]).FirstOrDefault()?.Summary?.Id;
    const planBooks = this.isSummary(summaryOrPlanBooks)
      ? (summaryOrPlanBooks as ITaskManagementPlanSummary).PlanBooks ?? []
      : summaryOrPlanBooks as ITaskManagementPlanBook[];
    this.basicFertilisationSummariesStore.deleteBasicFertilisationPlanBooks(
      summaryId, planBooks.map(x => x.Id), true, true);
  }

  public changesSaveStatus(): Observable<boolean> {
    return this.basicFertilisationSummariesStore.Listen(x => x.saved);
  }

  private getAppRateValue(planBook: ITaskManagementPlanBook, valueToTake: 'min' | 'max' | 'avg'): number {
    // Workaround for summaries/planbooks with the wrong element (might be due to migration from 4.8)
    const element = planBook.OperationMode?.Key === OperationModesEnum.Constant
    && planBook.OperationMode?.Key === OperationModesEnum.Constant
      ? this.nutrientService.getFertilizerLeadingElement(planBook.ProductId)
      : planBook.Element?.Id;
    const rate = valueToTake === 'avg'
      ? planBook.ApplRate
      : valueToTake === 'max' ? planBook.ApplMax : planBook.ApplMin;
    return this.nutrientService.convertNutrientToGoods(planBook.ProductId, element, rate);
  }
}
